import { gql } from "@apollo/client";
import {
  ConnectAccount,
  ConnectBalance,
  ConnectPayoutList,
  ConnectTransactionList,
  ConnectOnboardingResult,
  OnlinePaymentSummary,
  PaymentLinkResult,
  PaymentLinkList,
  PaymentLink,
} from "./models";

// Response types
export type GetConnectAccountResponse = {
  getConnectAccount: ConnectAccount | null;
};

export type GetConnectBalanceResponse = {
  getConnectBalance: ConnectBalance | null;
};

export type GetConnectPayoutsResponse = {
  getConnectPayouts: ConnectPayoutList;
};

export type GetConnectTransactionsResponse = {
  getConnectTransactions: ConnectTransactionList;
};

export type GetOnlinePaymentSummaryResponse = {
  getOnlinePaymentSummary: OnlinePaymentSummary;
};

export type CreateConnectAccountResponse = {
  createConnectAccount: ConnectOnboardingResult;
};

export type CreateConnectOnboardingLinkResponse = {
  createConnectOnboardingLink: ConnectOnboardingResult;
};

export type CreateConnectLoginLinkResponse = {
  createConnectLoginLink: ConnectOnboardingResult;
};

export type UnlinkConnectAccountResponse = {
  unlinkConnectAccount: boolean;
};

export type UpdateConnectAccountResponse = {
  updateConnectAccount: ConnectOnboardingResult;
};

// Queries
export const GET_CONNECT_ACCOUNT = gql`
  query GetConnectAccount {
    getConnectAccount {
      id
      status
      charges_enabled
      payouts_enabled
      details_submitted
      email
      business_profile_name
      commission_rate
    }
  }
`;

export const GET_CONNECT_BALANCE = gql`
  query GetConnectBalance {
    getConnectBalance {
      available
      pending
      currency
    }
  }
`;

export const GET_CONNECT_PAYOUTS = gql`
  query GetConnectPayouts($limit: Int, $starting_after: String) {
    getConnectPayouts(limit: $limit, starting_after: $starting_after) {
      data {
        id
        amount
        currency
        status
        arrival_date
        created
        description
        method
      }
      has_more
    }
  }
`;

export const GET_CONNECT_TRANSACTIONS = gql`
  query GetConnectTransactions($limit: Int, $starting_after: String) {
    getConnectTransactions(limit: $limit, starting_after: $starting_after) {
      data {
        id
        amount
        fee
        net
        currency
        type
        description
        created
        status
      }
      has_more
    }
  }
`;

export const GET_ONLINE_PAYMENT_SUMMARY = gql`
  query GetOnlinePaymentSummary(
    $startDate: String!
    $endDate: String!
    $branch_id: Int
  ) {
    getOnlinePaymentSummary(
      startDate: $startDate
      endDate: $endDate
      branch_id: $branch_id
    ) {
      totalRevenue
      totalFees
      totalNet
      totalOrders
      dailyBreakdown {
        date
        count
        total
        fees
        net
      }
    }
  }
`;

// Mutations
export const CREATE_CONNECT_ACCOUNT = gql`
  mutation CreateConnectAccount {
    createConnectAccount {
      url
    }
  }
`;

export const CREATE_CONNECT_ONBOARDING_LINK = gql`
  mutation CreateConnectOnboardingLink {
    createConnectOnboardingLink {
      url
    }
  }
`;

export const CREATE_CONNECT_LOGIN_LINK = gql`
  mutation CreateConnectLoginLink {
    createConnectLoginLink {
      url
    }
  }
`;

export const UNLINK_CONNECT_ACCOUNT = gql`
  mutation UnlinkConnectAccount {
    unlinkConnectAccount
  }
`;

export const UPDATE_CONNECT_ACCOUNT = gql`
  mutation UpdateConnectAccount {
    updateConnectAccount {
      url
    }
  }
`;

// --- Payment Links ---

export type GetPaymentLinksResponse = {
  getPaymentLinks: PaymentLinkList;
};

export type CreatePaymentLinkResponse = {
  createPaymentLink: PaymentLinkResult;
};

export type CancelPaymentLinkResponse = {
  cancelPaymentLink: PaymentLink;
};

export type ResendPaymentLinkResponse = {
  resendPaymentLink: PaymentLink;
};

export const GET_PAYMENT_LINKS = gql`
  query GetPaymentLinks($skip: Int, $take: Int, $status: PaymentLinkStatus) {
    getPaymentLinks(skip: $skip, take: $take, status: $status) {
      data {
        id
        amount
        description
        customer_email
        customer_name
        checkout_url
        status
        paid_at
        expires_at
        created_at
      }
      total
    }
  }
`;

export const CREATE_PAYMENT_LINK = gql`
  mutation CreatePaymentLink(
    $amount: Float!
    $description: String!
    $customer_email: String!
    $customer_name: String
  ) {
    createPaymentLink(
      amount: $amount
      description: $description
      customer_email: $customer_email
      customer_name: $customer_name
    ) {
      paymentLink {
        id
        amount
        description
        customer_email
        customer_name
        checkout_url
        status
        expires_at
        created_at
      }
      emailSent
    }
  }
`;

export const CANCEL_PAYMENT_LINK = gql`
  mutation CancelPaymentLink($id: Int!) {
    cancelPaymentLink(id: $id) {
      id
      status
    }
  }
`;

export const RESEND_PAYMENT_LINK = gql`
  mutation ResendPaymentLink($id: Int!) {
    resendPaymentLink(id: $id) {
      id
      status
    }
  }
`;
